import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, isSameMonth, isToday,
} from "date-fns";
import { it } from "date-fns/locale";
import { api } from "@/lib/hkApi";
import { CustomerAutocomplete } from "@/components/CustomerAutocomplete";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, LogIn, LogOut, Plus, Trash2, BedDouble } from "lucide-react";

export default function CalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [reservations, setReservations] = useState([]);
  const [selected, setSelected] = useState(format(new Date(), "yyyy-MM-dd"));
  const [rooms, setRooms] = useState([]);
  const [allRes, setAllRes] = useState([]);
  const [resRoom, setResRoom] = useState("");
  const [resGuest, setResGuest] = useState("");
  const [resFrom, setResFrom] = useState("");
  const [resTo, setResTo] = useState("");

  const load = useCallback(async () => {
    try {
      const [cal, rms, all] = await Promise.all([
        api.get("/calendar", { params: { month } }),
        api.get("/rooms"),
        api.get("/reservations"),
      ]);
      setReservations(cal.data.reservations);
      setRooms(rms.data);
      setAllRes(all.data);
    } catch {
      toast.error("Errore nel caricamento del calendario");
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const addRes = async () => {
    if (!resRoom || !resFrom || !resTo) { toast.info("Seleziona camera e date"); return; }
    try {
      await api.post("/reservations", { room_id: resRoom, summary: resGuest.trim() || "Prenotazione", start: resFrom, end: resTo });
      setResGuest(""); setResFrom(""); setResTo("");
      toast.success("Prenotazione aggiunta");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiunta prenotazione");
    }
  };

  const delRes = async (id) => {
    try {
      await api.delete(`/reservations/${id}`);
      toast.success("Prenotazione eliminata");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const mDate = parseISO(`${month}-01`);
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(mDate), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(mDate), { weekStartsOn: 1 }),
  });

  const arrivalsOn = (ds) => reservations.filter((r) => r.start === ds);
  const departuresOn = (ds) => reservations.filter((r) => r.end === ds);
  const selArrivals = arrivalsOn(selected);
  const selDepartures = departuresOn(selected);
  const selStays = reservations.filter((r) => r.start < selected && r.end > selected);

  return (
    <div data-testid="calendar-page" className="max-w-2xl mx-auto px-4 -mt-6">
      <div className="anim-in bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4">
        <div className="flex items-center gap-2 mb-3">
          <button data-testid="month-prev-btn" onClick={() => setMonth(format(addMonths(mDate, -1), "yyyy-MM"))} className="h-9 w-9 rounded-full border border-[#E8DCC8] flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <p className="flex-1 text-center font-display text-lg font-bold text-[#2B1410] capitalize" data-testid="current-month-label">
            {format(mDate, "MMMM yyyy", { locale: it })}
          </p>
          <button data-testid="month-next-btn" onClick={() => setMonth(format(addMonths(mDate, 1), "yyyy-MM"))} className="h-9 w-9 rounded-full border border-[#E8DCC8] flex items-center justify-center text-[#4A1C17] hover:bg-[#FAF3EE] transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-[#7C5A4E] mb-1">
          {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const ds = format(day, "yyyy-MM-dd");
            const arr = arrivalsOn(ds).length;
            const dep = departuresOn(ds).length;
            const inMonth = isSameMonth(day, mDate);
            const isSel = ds === selected;
            return (
              <button
                key={ds}
                data-testid={`cal-day-${ds}`}
                onClick={() => setSelected(ds)}
                className={`rounded-xl py-2 flex flex-col items-center gap-1 transition-all border ${
                  isSel ? "bg-[#4A1C17] text-[#F7EBDF] border-[#4A1C17]" :
                  isToday(day) ? "bg-[#FBF6E8] border-[#C5A059]/60 text-[#2B1410]" :
                  inMonth ? "bg-white border-[#E8DCC8] text-[#2B1410] hover:border-[#4A1C17]/40" :
                  "bg-transparent border-transparent text-[#7C5A4E]/40"
                }`}
              >
                <span className="text-sm font-semibold">{format(day, "d")}</span>
                <span className="flex gap-1 h-1.5">
                  {dep > 0 && <span className={`h-1.5 w-1.5 rounded-full ${isSel ? "bg-[#F7EBDF]" : "bg-[#4A1C17]"}`} />}
                  {arr > 0 && <span className={`h-1.5 w-1.5 rounded-full ${isSel ? "bg-[#C5A059]" : "bg-[#C5A059]"}`} />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 text-xs text-[#7C5A4E]">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#4A1C17]" /> Partenza</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#C5A059]" /> Arrivo</span>
        </div>
      </div>

      <div className="anim-in mt-4 bg-white border border-[#E8DCC8] rounded-2xl shadow-sm p-4 sm:p-5" data-testid="day-detail">
        <h3 className="font-display text-lg font-bold text-[#2B1410] capitalize">
          {format(parseISO(selected), "EEEE d MMMM", { locale: it })}
        </h3>
        <div className="mt-3 space-y-2">
          {selDepartures.map((r) => (
            <div key={`dep-${r.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#FAF3EE] border border-[#E8DCC8]" data-testid={`dep-${r.id}`}>
              <span className="h-8 w-8 rounded-lg bg-[#4A1C17] text-[#F7EBDF] flex items-center justify-center shrink-0"><LogOut className="h-4 w-4" /></span>
              <div className="text-sm"><span className="font-semibold text-[#2B1410]">{r.room_name}</span> <span className="text-[#7C5A4E]">· Partenza — {r.summary}</span></div>
            </div>
          ))}
          {selArrivals.map((r) => (
            <div key={`arr-${r.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#FBF6E8] border border-[#C5A059]/40" data-testid={`arr-${r.id}`}>
              <span className="h-8 w-8 rounded-lg bg-[#C5A059] text-white flex items-center justify-center shrink-0"><LogIn className="h-4 w-4" /></span>
              <div className="text-sm"><span className="font-semibold text-[#2B1410]">{r.room_name}</span> <span className="text-[#7C5A4E]">· Arrivo — {r.summary}</span></div>
            </div>
          ))}
          {selStays.map((r) => (
            <div key={`stay-${r.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-[#E8DCC8]" data-testid={`stay-${r.id}`}>
              <span className="h-8 w-8 rounded-lg bg-[#7C5A4E] text-[#F7EBDF] flex items-center justify-center shrink-0"><LogIn className="h-4 w-4" /></span>
              <div className="text-sm"><span className="font-semibold text-[#2B1410]">{r.room_name}</span> <span className="text-[#7C5A4E]">· Ospite in casa — {r.summary}</span></div>
            </div>
          ))}
          {selArrivals.length + selDepartures.length + selStays.length === 0 && (
            <p className="text-sm text-[#7C5A4E] py-2" data-testid="no-events-msg">Nessuna prenotazione in questo giorno.</p>
          )}
        </div>
        <button
          data-testid="go-to-day-btn"
          onClick={() => navigate(`/?date=${selected}`)}
          className="mt-4 rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-5 py-2 text-sm font-semibold transition-colors"
        >
          Apri checklist del giorno
        </button>
      </div>

      <div className="anim-in mt-4 bg-white border border-[#E8DCC8] rounded-2xl shadow-md p-4 sm:p-5" data-testid="new-reservation-card">
        <h3 className="font-display text-lg font-bold text-[#2B1410] flex items-center gap-2">
          <Plus className="h-5 w-5 text-[#4A1C17]" /> Nuova prenotazione
        </h3>
        <p className="text-sm text-[#7C5A4E] mt-1">Inserisci chi entra e chi esce: le checklist giornaliere si aggiornano da sole.</p>
        <div className="mt-3 space-y-2">
          <select
            data-testid="res-room-select"
            value={resRoom}
            onChange={(e) => setResRoom(e.target.value)}
            className="w-full rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
          >
            <option value="">Seleziona camera…</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.number}. {r.name}</option>)}
          </select>
          <CustomerAutocomplete
            testId="res-guest-input"
            value={resGuest}
            onChange={setResGuest}
            search={(q) => api.get(`${process.env.REACT_APP_BACKEND_URL}/api/customers/search`, { params: { q } }).then((r) => r.data)}
            onPick={(c) => { setResGuest(c.name); toast.success(`Ospite già in archivio: ${c.name}`); }}
            placeholder="Nome ospite (es. Famiglia Rossi)"
            className="w-full rounded-full border border-[#E8DCC8] bg-white px-4 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17] transition-colors"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <input data-testid="res-from" type="date" value={resFrom} onChange={(e) => setResFrom(e.target.value)} className="rounded-full border border-[#E8DCC8] bg-white px-3 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]" />
            <span className="text-[#7C5A4E] text-sm">→</span>
            <input data-testid="res-to" type="date" value={resTo} onChange={(e) => setResTo(e.target.value)} className="rounded-full border border-[#E8DCC8] bg-white px-3 py-2 text-sm text-[#2B1410] outline-none focus:border-[#4A1C17]" />
            <button data-testid="res-add-btn" onClick={addRes} className="rounded-full bg-[#4A1C17] hover:bg-[#2B1410] text-[#F7EBDF] px-5 py-2 text-sm font-semibold transition-colors">
              Aggiungi
            </button>
          </div>
        </div>
      </div>

      <div className="anim-in mt-4 bg-white border border-[#E8DCC8] rounded-2xl shadow-sm p-4 sm:p-5" data-testid="reservations-list">
        <h3 className="font-display text-lg font-bold text-[#2B1410]">Prenotazioni</h3>
        <div className="mt-3 space-y-2">
          {allRes.filter((r) => r.end >= format(new Date(), "yyyy-MM-dd")).map((r) => (
            <div key={r.id} data-testid={`res-row-${r.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#FAF3EE] border border-[#E8DCC8]">
              <span className="h-8 w-8 rounded-lg bg-[#4A1C17] text-[#F7EBDF] flex items-center justify-center shrink-0"><BedDouble className="h-4 w-4" /></span>
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-semibold text-[#2B1410]">{r.room_name}</span>
                <span className="text-[#7C5A4E]"> · {r.summary} · {format(parseISO(r.start), "d MMM", { locale: it })} → {format(parseISO(r.end), "d MMM", { locale: it })}</span>
              </div>
              <button data-testid={`res-delete-${r.id}`} onClick={() => delRes(r.id)} className="text-red-700 hover:bg-red-50 rounded-full p-1.5 transition-colors shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {allRes.filter((r) => r.end >= format(new Date(), "yyyy-MM-dd")).length === 0 && (
            <p className="text-sm text-[#7C5A4E] italic" data-testid="no-reservations-msg">Nessuna prenotazione inserita.</p>
          )}
        </div>
      </div>
    </div>
  );
}
